import { reportError } from "../../../utils/Instrumentation";
import { createApiClient } from "../../../api/apiClient";
import { AppDispatch } from "../../../store";
import { setQuestions } from "../../../store/slices/leadershipWalkRedirectSlice";
import {
  handleLeadershipWalkErrorWithFriendlyMessage,
  LEADERSHIP_WALK_API_NAMES,
} from "../../../store/utils/leadershipWalkErrorHandler";

/**
 * Fetches leadership walk questions based on the zone ID
 * @param frequency - Frequency of the walk
 * @param zoneIds - Zone IDs to fetch questions for
 * @param dispatch - Redux dispatch function
 * @param setError - Function to set error message
 * @param setLoading - Function to toggle loading state
 */
export const fetchQuestions = async (
  frequency: string,
  zoneIds: string | number[],
  dispatch: AppDispatch,
  setError: (error: string | null) => void,
  setLoading: (loading: boolean) => void
) => {
  console.log("fetchQuestions called with:", { frequency, zoneIds });

  if (!zoneIds) {
    setError("Invalid Zone ID");
    setLoading(false);
    return;
  }

  setError(null);
  setLoading(true);

  try {
    const api = await createApiClient();
    console.log("API client created");

    const walkType = "Leadership Walk";
    const endpoint = `/leadership-walks/questions?walkType=${encodeURIComponent(
      walkType
    )}&frequency=${encodeURIComponent(frequency)}&zoneIds=${zoneIds}`;
    console.log("Calling API with endpoint:", endpoint);

    const response = await api.get(endpoint);
    console.log("API response received:", response.data);

    if (!response?.data) {
      throw new Error("Invalid response from server");
    }

    const questionsResponse = response.data;

    // Check if the response is already organized by category
    if (
      questionsResponse &&
      typeof questionsResponse === "object" &&
      !Array.isArray(questionsResponse)
    ) {
      console.log("Response is already organized by category");

      // The API already returns an organized object with category keys
      const organizedQuestions = {
        Safety: questionsResponse.Safety || [],
        People: questionsResponse.People || [],
        Accuracy: questionsResponse.Accuracy || [],
        Costs: questionsResponse.Costs || [],
        Reliability: questionsResponse.Reliability || [],
      };

      console.log("Organized questions:", organizedQuestions);

      // Update Redux store with organized questions
      dispatch(setQuestions(organizedQuestions));
    } else if (Array.isArray(questionsResponse)) {
      // If the API returns an array of questions, organize them by section
      console.log("Response is an array, organizing by category");

      const organizedQuestions = {
        Safety: questionsResponse.filter(
          (q: { Section: string }) => q.Section === "Safety"
        ),
        People: questionsResponse.filter(
          (q: { Section: string }) => q.Section === "People"
        ),
        Accuracy: questionsResponse.filter(
          (q: { Section: string }) => q.Section === "Accuracy"
        ),
        Costs: questionsResponse.filter(
          (q: { Section: string }) => q.Section === "Costs"
        ),
        Reliability: questionsResponse.filter(
          (q: { Section: string }) => q.Section === "Reliability"
        ),
      };

      console.log("Organized questions:", organizedQuestions);

      // Update Redux store with organized questions
      dispatch(setQuestions(organizedQuestions));
    } else {
      // Handle unexpected response format
      const unexpectedFormatError = new Error("Unexpected response format from server");
      
      handleLeadershipWalkErrorWithFriendlyMessage(
        LEADERSHIP_WALK_API_NAMES.FETCH_QUESTIONS,
        unexpectedFormatError,
        true,
        { frequency, zoneIds, reason: "Unexpected response format" }
      );
      
      setError("Unexpected response format from server");
    }
    setLoading(false);
  } catch (error) {
    console.error("Error fetching leadership walk questions:", error);
    reportError({
      severity: "ERROR",
      message: error instanceof Error ? error.message : String(error),
      stackTrace: error instanceof Error ? error.stack : undefined,
    });
    
    // Use centralized error handler with modal overlay
    handleLeadershipWalkErrorWithFriendlyMessage(
      LEADERSHIP_WALK_API_NAMES.FETCH_QUESTIONS,
      error,
      true,
      { frequency, zoneIds }
    );
    
    setError(
      error instanceof Error ? error.message : "Failed to fetch questions"
    );
    setLoading(false);
  }
};
